import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import styled from 'styled-components';

import {
  defaultFirmImageUrl,
  defaultProjectImageUrl,
} from '../../services/APIClient';
import {
  ANALYTICS_NAME,
  ANALYTICS_TYPE,
} from './Constants';
import { PERMISSION_TYPES } from '../RequireRegisterPopup';
import { MEDIA_QUERIES } from '../../services/constants';

import ImageOverPlaceholder, { ASPECT_RATIOS } from '../ImageOverPlaceholder';
import APlusBadge from './APlusBadge';

const ProjectAction = styled.a`
  && {
    display: block;
    z-index: 1;
    cursor: pointer;
    background: rgba(10,10,10,.4);
    position: absolute;
    top: 0;
    color: rgba(255,255,255,.6);
    width: 2rem;
    height: 2rem;
    line-height: 2;
    text-align: center;
    transition: background .3s, color .3s, opacity .3s;
    opacity: 0;
    
    &:hover {
      background: rgba(10,10,10,.65);
      color: white;
    }
  }
`;

const AddToCollectionButton = styled(ProjectAction)`
  right: 4px;
`;

const ImageContainer = styled.div`
  & {
    position: relative;

    &:hover ${ProjectAction} {
      opacity: 1;
    }
  }
`;

const FirmLogo = styled.div`
  width: 90px;
  height: 90px;
  min-width: 90px;
  min-height: 90px;

  @media screen and (max-width: ${MEDIA_QUERIES.small}) {
    width: 35px;
    height: 35px;
    min-width: 35px;
    min-height: 35px;
  }
`;

const Firm = ({
  firm,
  hasDivider,
  sendAnalytics,
  canCollect,
  showImageActions,
  openInNewTab,
  openRequireRegisterPopup,
}) => (
  <>
    <div className={classNames(
      'columns',
      'small-12',
      { 'mb-base': !hasDivider },
    )}
    >
      <div className="row">
        {/* Details */}
        <div className="columns small-12 medium-6 large-5 xlarge-6 mb-base">
          {/* Media */}
          <FirmLogo className="float-left mr-base">
            <ImageOverPlaceholder
              aspectRatio={ASPECT_RATIOS.SQUARE}
              image={{ url: firm.media, width: 200, height: 200 }}
              placeholder={defaultFirmImageUrl}
              rounded
              href={`/firms/${firm.slug}/`}
              target={openInNewTab ? '_blank' : '_self'}
              onClick={() => {
                sendAnalytics(ANALYTICS_NAME.RESULT_CLICKED, {
                  position: firm.index,
                  type: ANALYTICS_TYPE.FIRM_PROFILE,
                });
              }}
            />
          </FirmLogo>
          {/* Info */}
          <div className="ellipsis">
            <a
              className="black fw-medium ellipsis flex-container flex-dir-row"
              style={{ lineHeight: '1.2rem', height: '2rem' }}
              href={`/firms/${firm.slug}/`}
              target={openInNewTab ? '_blank' : '_self'}
              onClick={() => {
                sendAnalytics(ANALYTICS_NAME.RESULT_CLICKED, {
                  position: firm.index,
                  type: ANALYTICS_TYPE.FIRM_PROFILE,
                });
              }}
            >
              <div>
                {firm.name}
              </div>

              {!firm.showAPlusBadge ? '' : (
                <APlusBadge />
              )}

            </a>

            {firm.projectsCount
              ? (
                <p className="fw-medium fs-s m-0 ellipsis">
                  {firm.projectsCount} Project{firm.projectsCount > 1 ? 's' : ''}
                </p>
              ) : <></>}
            <div>
              <a
                className="button tiny mt-xxs mr-xxs"
                href={`/firms/${firm.slug}/`}
                target={openInNewTab ? '_blank' : '_self'}
                onClick={() => {
                  sendAnalytics(ANALYTICS_NAME.RESULT_CLICKED, {
                    position: firm.index,
                    type: ANALYTICS_TYPE.CONTACT,
                  });
                }}
              >
                Contact <i className="material-icons fs-s">chat_bubble</i>
              </a>
              <a
                className="button secondary tiny mt-xxs"
                href={`/image-search/popular-images/firm=${encodeURIComponent(firm.name)}`}
                target={openInNewTab ? '_blank' : '_self'}
                onClick={() => {
                  sendAnalytics(ANALYTICS_NAME.RESULT_CLICKED, {
                    position: firm.index,
                    type: ANALYTICS_TYPE.IMAGE_SEARCH,
                  });
                }}
              >
                Images <i className="material-icons fs-s">image</i>
              </a>
            </div>
          </div>
        </div>
        {/* Projects Gallery */}
        <div className="columns small-12 medium-6 large-7 xlarge-6 mb-base">
          <div className="row gutter-xxxs align-right small-up-2 medium-up-3 large-up-5 pr-xs pl-xs">
            {firm.projects.filter(e => !e.isHiddenFromListings).map((project, i) => (
              <ImageContainer
                className={classNames(
                  'columns',
                  { 'show-for-large': i > 2 },
                  { 'show-for-medium': i === 2 },
                  { 'show-for-small': i < 2 },
                )}
                key={project.slug}
              >
                {showImageActions && project.mediaId
                  && (
                    <>
                      <AddToCollectionButton
                        href={null}
                        onClick={(event) => {
                          event.stopPropagation();
                          if (canCollect) {
                            window.openAddToCollectionPopup({ mediaAttrId: `${project.mediaId}` });
                          } else {
                            openRequireRegisterPopup({
                              permission: PERMISSION_TYPES.COLLECT,
                              id: project.mediaId,
                            });
                          }
                        }}
                      >
                        <i className="material-icons">playlist_add</i>
                      </AddToCollectionButton>
                    </>
                  )}
                <ImageOverPlaceholder
                  image={{ url: project.media, width: 200, height: 150 }}
                  placeholder={defaultProjectImageUrl}
                  aspectRatio={ASPECT_RATIOS.STANDARD}
                  href={`/projects/${project.slug}/`}
                  target={openInNewTab ? '_blank' : '_self'}
                  onClick={() => {
                    sendAnalytics(ANALYTICS_NAME.RESULT_CLICKED, {
                      position: firm.index,
                      type: ANALYTICS_TYPE.PROJECT_CARD,
                    });
                  }}
                />
              </ImageContainer>
            ))}
          </div>
        </div>
      </div>
    </div>
    {/* Divider */}
    {hasDivider && (
    <div className="columns small-12">
      <hr className="border-global-separator mt-0" />
    </div>
    )}
  </>
);

Firm.propTypes = {
  firm: PropTypes.shape({
    slug: PropTypes.string,
    name: PropTypes.string,
    location: PropTypes.string,
    projectsCount: PropTypes.number,
    lastProjectDate: PropTypes.string,
    description: PropTypes.string,
    media: PropTypes.string,
    projects: PropTypes.arrayOf(PropTypes.shape({
      slug: PropTypes.string,
      media: PropTypes.string,
      mediaId: PropTypes.number,
    })),
  }),
  hasDivider: PropTypes.bool,
  sendAnalytics: PropTypes.func,
  canCollect: PropTypes.bool,
  showImageActions: PropTypes.bool,
  openInNewTab: PropTypes.bool,
  showAPlusBadge: PropTypes.bool,
  openRequireRegisterPopup: PropTypes.func,
};
Firm.defaultProps = {
  firm: {
    slug: '',
    name: '',
    location: '',
    projectsCount: 0,
    lastProjectDate: null,
    description: '',
    media: defaultFirmImageUrl,
    projects: [{
      name: '',
      media: defaultProjectImageUrl,
      mediaId: null,
    }],
  },
  hasDivider: false,
  sendAnalytics: () => {},
  canCollect: false,
  showImageActions: true,
  openInNewTab: false,
  showAPlusBadge: false,
  openRequireRegisterPopup: () => {},
};

export default Firm;
